import { FC } from 'react';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import { IProviderUpload, UploadFailureReason, UploadStage } from 'types/upload-status';

const STAGE_LABELS: Record<UploadStage, string> = {
	RECEIVING: 'Завантаження файла',
	PARSING: 'Обробка файла',
	COMPLETED: 'Файл повністю прочитано',
	INCOMPLETE: 'Дані неповні: файл перевищує ліміт 400 МБ і був обрізаний',
	FAILED: 'Не вдалося завершити завантаження та обробку файла. Дані неповні',
};

const FAILURE_LABELS: Record<UploadFailureReason, string> = {
	CONNECTION_CLOSED:
		'З’єднання обірвалося до завершення передачі файла. Перевірте з’єднання та завантажте файл ще раз.',
	UPLOAD_FAILED: 'Помилка під час прийому файла. Спробуйте завантажити файл ще раз.',
	PARSING_ABORTED: 'Обробку файла перервано. Файл потрібно завантажити ще раз.',
	NO_FILE: 'У запиті не було файла. Виберіть файл і спробуйте ще раз.',
	NO_OUTCOME: 'За 3 години результат не отримано. Стан невідомий. Перевірте результат перед повторним завантаженням.',
};

const formatBytes = (bytes: number): string =>
	`${(bytes / 1024 / 1024).toLocaleString('uk-UA', { maximumFractionDigits: 2 })} МБ`;

export const UploadProgress: FC<IProviderUpload> = ({ status, request }) => {
	if (!status && !request) return null;
	const percent =
		status?.bytesTotal && status.percent !== undefined && Number.isFinite(status.percent)
			? Math.min(100, Math.max(0, status.percent))
			: undefined;
	const failed = status?.stage === 'FAILED';
	const incomplete = status?.stage === 'INCOMPLETE';
	const completed = status?.stage === 'COMPLETED';
	const receiving = status?.stage === 'RECEIVING';
	const parsing = status?.stage === 'PARSING';
	const unknownOutcome = failed && status.failureReason === 'NO_OUTCOME';
	const fileNames = status?.fileNames.length ? status.fileNames.join(', ') : request?.fileName;

	return (
		<Box sx={{ mt: 1, mb: 2, overflowWrap: 'anywhere' }}>
			<Typography variant="body2">{fileNames}</Typography>
			<Box role="status" aria-live="polite">
				{status ? (
					<>
						<Typography
							variant="body2"
							color={failed ? 'error.main' : incomplete ? 'warning.main' : completed ? 'success.main' : 'text.primary'}
						>
							{unknownOutcome ? 'Результат обробки невідомий' : STAGE_LABELS[status.stage]}
							{receiving && percent !== undefined && ` — ${Math.round(percent)}%`}
						</Typography>
						{(receiving || parsing) && (
							<LinearProgress
								aria-label={parsing ? 'Обробка файла' : 'Завантаження файла'}
								variant={receiving && percent !== undefined ? 'determinate' : 'indeterminate'}
								value={percent}
								sx={{ my: 1 }}
							/>
						)}
						{receiving && (
							<Typography variant="body2">
								Отримано {formatBytes(status.bytesReceived)}
								{status.bytesTotal > 0 && ` з ${formatBytes(status.bytesTotal)}`}
							</Typography>
						)}
						{!receiving && (
							<Typography variant="body2">Передано в ETL: {status.rowsSent.toLocaleString('uk-UA')} рядків</Typography>
						)}
						{receiving && status.stalled && (
							<Alert severity="warning">Дані не надходять понад 30 секунд. Перевірте з’єднання.</Alert>
						)}
						{status.truncated && !incomplete && (
							<Alert severity="warning">Файл обрізано за лімітом 400 МБ. Дані неповні.</Alert>
						)}
						{failed && (
							<Alert severity="error">
								{status.failureReason ? FAILURE_LABELS[status.failureReason] : 'Файл потрібно завантажити ще раз.'}
							</Alert>
						)}
						{incomplete && (
							<Alert severity="warning">Підготуйте файл у межах ліміту 400 МБ і завантажте його повторно.</Alert>
						)}
					</>
				) : request?.state === 'error' ? (
					<Alert severity="error">
						Не вдалося підтвердити прийом файла. Перевірте з’єднання та стан обробки перед повторною спробою.
					</Alert>
				) : (
					<>
						<Typography variant="body2">
							{request?.state === 'accepted'
								? 'Файл прийнято. Очікуємо результат обробки…'
								: 'Очікуємо початку завантаження…'}
						</Typography>
						<LinearProgress aria-label="Очікування статусу файла" sx={{ my: 1 }} />
					</>
				)}
			</Box>
			{request?.state === 'error' && status && !completed && !failed && !incomplete && (
				<Alert severity="warning">
					Запит завершився помилкою. Очікуємо результат обробки; не завантажуйте файл повторно до уточнення стану.
				</Alert>
			)}
			{status && (
				<Box component="details" sx={{ mt: 1 }}>
					<Box component="summary" sx={{ cursor: 'pointer' }}>
						Технічні деталі
					</Box>
					<Typography variant="body2">ID: {status.uploadId}</Typography>
					<Typography variant="body2">Передано блоків: {status.chunksSent}</Typography>
					<Typography variant="body2">
						Тривалість: {Math.round(status.elapsedMs / 1000)} с. Без даних: {Math.round(status.idleMs / 1000)} с.
					</Typography>
					<Box sx={{ maxHeight: 240, overflow: 'auto' }}>
						{status.log.map((entry, index) => (
							<Typography
								key={`${entry.at}-${index}`}
								variant="body2"
								sx={{ whiteSpace: 'pre-wrap' }}
								color={
									entry.level === 'error' ? 'error.main' : entry.level === 'warn' ? 'warning.main' : 'text.secondary'
								}
							>
								{entry.at} [{entry.level}] {entry.message}
							</Typography>
						))}
					</Box>
				</Box>
			)}
		</Box>
	);
};
